import { useState } from 'react'
import { API_URL, post, put } from '../../../api'
import { Alert, Button, Card, Field } from '../../../components/ui'
import { useAction } from '../../../hooks'
import { encodeQr } from '../../../qr'
import { useBusiness } from '../Layout'

// The embed snippet, the allowed websites, the hosted booking page and key rotation.

const WIDGET_URL = (import.meta.env.VITE_WIDGET_URL as string | undefined)?.replace(/\/$/, '') ?? 'http://localhost:5173'
const BOOKING_PAGE_URL = (import.meta.env.VITE_BOOKING_PAGE_URL as string | undefined)?.replace(/\/$/, '') ?? WIDGET_URL

export function WebsiteSection() {
  const { business, setBusiness, reload, can } = useBusiness()
  const [origins, setOrigins] = useState(business.origins.join('\n'))
  const [copied, setCopied] = useState<string | null>(null)
  const saveOrigins = useAction(async () => {
    const list = origins
      .split(/\s+/)
      .map((o) => o.trim())
      .filter(Boolean)
      .map((o) => (o.startsWith('http') ? o : `https://${o}`))
    await put(`/v1/admin/businesses/${business.id}/embed-domains`, { origins: list })
    await reload({ quiet: true })
  })
  const rotate = useAction(async () => {
    if (!confirm('Rotate the key? The booking button on your website stops working until the new snippet is installed.')) return
    const r = await post<{ publicKey: string }>(`/v1/admin/businesses/${business.id}/public-key/rotate`)
    setBusiness({ ...business, publicKey: r.publicKey })
  })
  const copy = async (what: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(what)
      setTimeout(() => setCopied(null), 2000)
    } catch {
      /* clipboard blocked; the text is selectable */
    }
  }

  const snippet = `<script async src="${WIDGET_URL}/v1.js"
  data-business="${business.publicKey}"
  data-api="${API_URL}"></script>
<button data-booking-open>Book a table</button>`
  const pageUrl = `${BOOKING_PAGE_URL}/${business.slug}`
  const qr = encodeQr(pageUrl)
  const owner = can('owner')

  return (
    <div className="stack-lg">
      <Card
        title="Add the booking button to your website"
        actions={
          <Button size="sm" onClick={() => void copy('snippet', snippet)}>
            {copied === 'snippet' ? 'Copied' : 'Copy'}
          </Button>
        }
      >
        <p className="small" style={{ marginBottom: 10 }}>
          Paste this where you want the button. Any element with <code>data-booking-open</code> opens the booking window on your own page; guests never leave your site. Works with WordPress, Squarespace, Wix (custom code), Webflow and plain HTML.
        </p>
        <pre className="code-block">{snippet}</pre>
        <p className="hint">
          Optional attributes on the script: <code>data-theme="light|dark|auto"</code>, <code>data-lang="en|tr|auto"</code>, <code>data-accent="#1f4e3d"</code>. Add <code>data-booking-source="menu-page"</code> to a button to see where bookings come from.
        </p>
      </Card>

      <Card title="Websites allowed to show the button">
        <p className="small" style={{ marginBottom: 10 }}>
          One per line, e.g. <code>https://www.yourrestaurant.com</code>. Only these sites can take bookings with your key. Leave empty while setting up to allow any site.
        </p>
        <Field label="Allowed websites">{(id) => <textarea id={id} className="input" rows={3} value={origins} onChange={(e) => setOrigins(e.target.value)} disabled={!owner} spellCheck={false} />}</Field>
        {saveOrigins.error ? <Alert>{saveOrigins.error}</Alert> : null}
        {owner ? (
          <div className="row-flex" style={{ marginTop: 10 }}>
            <Button variant="primary" busy={saveOrigins.busy} onClick={() => void saveOrigins.run()}>
              Save
            </Button>
          </div>
        ) : null}
      </Card>

      <Card title="Booking page without a website">
        <div className="row-flex" style={{ alignItems: 'flex-start' }}>
          <div className="grow stack">
            <p className="small">
              Guests can also book at{' '}
              <a href={pageUrl} target="_blank" rel="noreferrer">
                {pageUrl}
              </a>
              . Use it on Instagram, Google Business Profile, menus and table cards.
            </p>
            <div className="row-flex">
              <Button size="sm" onClick={() => void copy('page', pageUrl)}>
                {copied === 'page' ? 'Copied' : 'Copy link'}
              </Button>
            </div>
          </div>
          {qr ? <QrSvg modules={qr} label="QR code for the booking page" /> : null}
        </div>
      </Card>

      <Card title="Your public key">
        <p className="small">
          <code className="mono">{business.publicKey}</code>
        </p>
        <p className="hint">The key only identifies your restaurant to the widget; it cannot read bookings. Rotate it if it was used somewhere it should not be.</p>
        {owner ? (
          <div className="row-flex" style={{ marginTop: 10 }}>
            <Button variant="danger" size="sm" busy={rotate.busy} onClick={() => void rotate.run()}>
              Rotate key
            </Button>
            {rotate.error ? <span className="error">{rotate.error}</span> : null}
          </div>
        ) : null}
      </Card>
    </div>
  )
}

function QrSvg({ modules, label }: { modules: boolean[][]; label: string }) {
  const n = modules.length
  const size = 132
  const cell = size / (n + 8)
  let path = ''
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) if (modules[y]![x]) path += `M${(x + 4) * cell} ${(y + 4) * cell}h${cell}v${cell}h-${cell}z`
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size * 4}" height="${size * 4}" viewBox="0 0 ${size} ${size}"><rect width="${size}" height="${size}" fill="#fff"/><path d="${path}" fill="#000"/></svg>`
  return (
    <a href={`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`} download="booking-qr.svg" title="Download as SVG">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={label} style={{ background: '#fff', borderRadius: 8, display: 'block' }}>
        <path d={path} fill="#000" />
      </svg>
    </a>
  )
}
